import {
  CAROUSEL_TARGET_PATIENT,
  CAROUSEL_TARGET_PROFESSIONAL,
  CAROUSEL_TARGET_FACILITY,
} from "./mapCarouselConstants";
import { normalizeGtgTargetAudience } from "@healthdesk/shared-utils";
import { getMapCarouselViewerSegment } from "./mapCarouselViewerSegment";
import {
  LISTING_TYPE_LOCAL_CAROUSEL,
  LOCAL_CAROUSEL_GENDER_ALL,
  LOCAL_CAROUSEL_GENDER_FEMALE,
  LOCAL_CAROUSEL_GENDER_MALE,
  normalizeLocalCarouselGender,
} from "./localCarouselFacilityConstants";

/**
 * Normalize sex/gender from user profile for carousel targeting.
 * Returns null when unknown — callers should treat as "show all" local slides.
 * @param {Record<string, unknown> | null | undefined} userData
 * @returns {typeof LOCAL_CAROUSEL_GENDER_MALE | typeof LOCAL_CAROUSEL_GENDER_FEMALE | null}
 */
export function getNormalizedPatientSexForCarousel(userData) {
  if (!userData || typeof userData !== "object") {
    return null;
  }
  const raw =
    userData.sex ??
    userData.gender ??
    userData.patientSex ??
    userData.patientGender ??
    (typeof userData.demographics === "object" && userData.demographics
      ? userData.demographics.sex ?? userData.demographics.gender
      : null);

  if (raw == null || typeof raw !== "string") {
    return null;
  }

  const t = raw.trim().toLowerCase();
  if (!t) {
    return null;
  }

  if (
    t === "m" ||
    t === "male" ||
    t === "man" ||
    t.startsWith("male ") ||
    t === "masculine"
  ) {
    return LOCAL_CAROUSEL_GENDER_MALE;
  }
  if (
    t === "f" ||
    t === "female" ||
    t === "woman" ||
    t.startsWith("female ") ||
    t === "feminine"
  ) {
    return LOCAL_CAROUSEL_GENDER_FEMALE;
  }

  return null;
}

/**
 * @param {typeof LOCAL_CAROUSEL_GENDER_ALL | typeof LOCAL_CAROUSEL_GENDER_MALE | typeof LOCAL_CAROUSEL_GENDER_FEMALE} slideGender
 * @param {typeof LOCAL_CAROUSEL_GENDER_MALE | typeof LOCAL_CAROUSEL_GENDER_FEMALE | null} viewerSex
 * @returns {boolean}
 */
export function localCarouselSlideVisibleForViewerSex(slideGender, viewerSex) {
  const g = normalizeLocalCarouselGender(slideGender);
  if (g === LOCAL_CAROUSEL_GENDER_ALL) {
    return true;
  }
  if (viewerSex == null) {
    return true;
  }
  return g === viewerSex;
}

/**
 * Maps CareMap viewer segment to GTG `targetAudience` value on listings.
 * @param {string | null | undefined} viewerSegment
 * @returns {'patient'|'provider'|'facility'}
 */
export function gtgTargetAudienceForViewerSegment(viewerSegment) {
  if (viewerSegment === CAROUSEL_TARGET_PROFESSIONAL) {
    return "provider";
  }
  if (viewerSegment === CAROUSEL_TARGET_FACILITY) {
    return "facility";
  }
  return "patient";
}

/**
 * @deprecated Prefer {@link gtgTargetAudienceForViewerSegment} + GTG audience on each listing.
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function isPatientViewerForLocalCarousel(role) {
  return getMapCarouselViewerSegment(role) === CAROUSEL_TARGET_PATIENT;
}

/**
 * @param {object} ad
 * @returns {boolean}
 */
export function isLocalCarouselAdType(ad) {
  return ad && ad.type === LISTING_TYPE_LOCAL_CAROUSEL;
}

/**
 * @param {object} ad
 * @param {string | null | undefined} role
 * @param {Record<string, unknown> | null | undefined} userData
 * @returns {boolean}
 */
export function localCarouselAdVisibleOnMap(ad, role, userData) {
  if (!isLocalCarouselAdType(ad)) {
    return true;
  }
  const viewerSegment = getMapCarouselViewerSegment(role);
  const want = gtgTargetAudienceForViewerSegment(viewerSegment);
  const adAudience = normalizeGtgTargetAudience(ad.gtgTargetAudience);
  if (adAudience !== want) {
    return false;
  }
  if (adAudience !== "patient") {
    return true;
  }
  const slideGender = normalizeLocalCarouselGender(
    ad.patientGender ?? ad.patientSegments?.gender
  );
  const viewerSex = getNormalizedPatientSexForCarousel(userData);
  return localCarouselSlideVisibleForViewerSex(slideGender, viewerSex);
}

/**
 * For tests / debugging: segment label
 * @param {string | null | undefined} role
 * @returns {string}
 */
export function getViewerSegmentLabelForTests(role) {
  const s = getMapCarouselViewerSegment(role);
  if (s === CAROUSEL_TARGET_PROFESSIONAL) return "professional";
  if (s === CAROUSEL_TARGET_FACILITY) return "facility";
  return "patient";
}
